import { useEffect, useRef } from "react";
import { getMaxDayForDob, MonthsMapOption } from "@app/components/dob/months";
import { isPatientMinor } from "@app/utils";

export interface UseDobValidationOptions {
  setValidationError: (field: string, message: string) => void;
  clearValidationError: (field: string) => void;
  invalidDayMessage: string;
  under18Message: string;
  validateAge: boolean;
  lowerYearLimit: number;
  currentYear: number;
  /** Called when full date is valid and not under 18 to clear extra errors and pass value to parent */
  onValidFullDate?: (dateValue: string) => void;
}

export const useDobValidation = (
  selectedMonth: MonthsMapOption | null,
  selectedDay: number | undefined,
  year: string,
  options: UseDobValidationOptions
): void => {
  const {
    setValidationError,
    clearValidationError,
    invalidDayMessage,
    under18Message,
    validateAge,
    lowerYearLimit,
    currentYear,
    onValidFullDate,
  } = options;

  const onValidFullDateRef = useRef(onValidFullDate);
  onValidFullDateRef.current = onValidFullDate;

  useEffect(() => {
    const isYearValid = (year: string): boolean => {
      if (year.length !== 4) return false;
      const numericYear = parseInt(year, 10);
      return (
        !isNaN(numericYear) &&
        numericYear >= lowerYearLimit &&
        numericYear <= currentYear
      );
    };

    if (!selectedMonth || !isYearValid(year)) {
      return;
    }

    const numericYear = Number(year);
    const maxDay = getMaxDayForDob(selectedMonth.value, numericYear);

    if (selectedDay !== undefined) {
      if (selectedDay > maxDay) {
        setValidationError("dateOfBirthDay", invalidDayMessage);
        return;
      }

      const monthValue = selectedMonth.value;
      const dateValue = `${year}-${monthValue.toString().padStart(2, "0")}-${selectedDay.toString().padStart(2, "0")}`;
      const dOB = new Date(dateValue);
      const minor = validateAge && isPatientMinor(dOB);

      if (minor) {
        setValidationError("dateOfBirthYear", under18Message);
        return;
      }

      clearValidationError("dateOfBirthDay");
      clearValidationError("dateOfBirthYear");
      onValidFullDateRef.current?.(dateValue);
      return;
    }

    clearValidationError("dateOfBirthYear");
  }, [
    selectedMonth,
    selectedDay,
    year,
    validateAge,
    lowerYearLimit,
    currentYear,
    invalidDayMessage,
    under18Message,
  ]);
};
